import { riToLevel, type Level } from "@/shared/lib/levelTone";
import type { Alert } from "@/shared/api/models";

/**
 * nodeRisk — 함체(센서노드) 하나의 **층·가스·등급**을 푸는 공용 규칙 (2026-09-29 관제 개편 P0).
 *
 * 관제 화면의 세 자리(함체 타일 · 노드별 위험도 패널 · 3D 표식)가 같은 함체를 서로 다른
 * 색으로 칠하면 현장과 관제가 다른 말을 한다. 그래서 「이 가스는 어느 층인가」「이 함체는
 * 몇 등급인가」를 여기 한 곳에서만 정한다. 원래 NodeTierTile 안에 있던 표를 끌어올린 것이다.
 *
 * 등급 합성 규칙: **max(미해제 가스 경보 등급, RI 등급)**. 경보는 서버 판정이고 RI 는
 * 엔진의 연속값이라, 한쪽만 보면 「경보는 떴는데 초록」이나 「RI 는 주의인데 회색」이 생긴다.
 * ⛔ 설비 무응답(health) 경보는 가스 위험이 아니므로 등급에 넣지 않는다(결정 11).
 * ⛔ 멈춘(stale) RI 는 넣지 않는다 — 호출 쪽이 `null` 을 넘긴다(RISK_FRESH_MS 참조).
 */

/** 층 키 — 위에서 아래로. 매핑에 없는 채널은 「기타」로 떨어진다. */
export type Tier = "top" | "mid" | "bot" | "etc";

export const TIER_ORDER: readonly Tier[] = ["top", "mid", "bot", "etc"];

/**
 * 가스 → 층. 정본은 에어위드 2026-08-31 조합표이며
 * `field/sensor/reader/rs485_box_reader.py` REGISTER_MAP,
 * `field/manager/Dadong.App/Utils/SensorTierMap.cs` 와 일치한다.
 * ⛔ 매핑을 바꿀 일이 생기면 매니져 쪽 SensorTierMap 과 **같이** 고친다.
 */
export const TIER_OF: Readonly<Record<string, Tier>> = {
  NH3: "top",
  CH4: "top",
  O2: "mid",
  CO: "mid",
  VOC: "mid",
  CO2: "bot",
  H2S: "bot",
  SO2: "bot",
  NO2: "bot",
};

/** 층 안에서의 표시 순서 — 현장 함체의 배선 순서와 같게 둔다. */
export const GAS_ORDER_IN_TIER: readonly string[] = ["NH3", "CH4", "O2", "CO", "VOC", "CO2", "H2S", "SO2", "NO2"];

/**
 * 가스 코드 정규화 — 대문자 ASCII. 화면용 기호(「O₂」「H₂S」)와 서버 코드(「O2」「h2s」)가
 * 같은 키로 만나게 아래첨자 숫자를 보통 숫자로 풀고 공백을 뺀다.
 * 엔진의 `contributors`·`gases` 키, 판독 행의 `gas`, 경보의 `gas` 를 비교하기 전에 쓴다.
 */
export function normGas(code: string | null | undefined): string {
  if (!code) return "";
  return code
    .replace(/[₀-₉]/g, (d) => String(d.charCodeAt(0) - 0x2080))
    .replace(/\s+/g, "")
    .toUpperCase();
}

export function tierOf(gas: string): Tier {
  return TIER_OF[normGas(gas)] ?? "etc";
}

/** 층 안 표시 순번 — 표에 없는 채널은 맨 뒤. */
export function gasOrderIndex(gas: string): number {
  const i = GAS_ORDER_IN_TIER.indexOf(normGas(gas));
  return i < 0 ? GAS_ORDER_IN_TIER.length : i;
}

const clampLevel = (lv: number | null | undefined): Level =>
  Math.min(3, Math.max(0, Math.trunc(Number(lv ?? 0)) || 0)) as Level;

/** 이 경보가 이 함체의 가스 등급에 들어가는가 — 해제·설비 무응답은 뺀다. */
const countsForGas = (a: Alert) => a.state !== "cleared" && a.kind !== "health";

/**
 * 채널 색 — 서버가 **이 노드의** 그 가스로 띄운 미해제 경보만 근거로 삼는다
 * (NodeTierTile `levelForGas` 와 같은 규칙).
 *
 * ⚠️ `a.node` 를 보지 않으면 현장에 경보 하나만 떠도 같은 채널을 가진 함체가
 * 전부 빨개진다. 노드가 지정되지 않은 현장 단위 경보(`node == null`)는 여기서 쓰지
 * 않는다 — 그것은 KPI 스트립과 경보 요약이 이미 말한다.
 * 해제된 경보·설비 무응답은 넘어와도 세지 않는다(미해제만 넘기는 호출부는 결과 동일).
 */
export function gasAlertLevel(gas: string, nodeId: number, alerts: readonly Alert[]): Level {
  const key = normGas(gas);
  let worst: Level = 0;
  for (const a of alerts) {
    if (a.node !== nodeId || !countsForGas(a)) continue;
    if (normGas(a.gas) !== key) continue;
    const lv = clampLevel(a.level);
    if (lv > worst) worst = lv;
  }
  return worst;
}

/**
 * 함체 등급(경보 쪽) — 이 노드에 걸린 미해제 가스 경보 중 최고치. **가스 종류로 거르지
 * 않는다** — 현장 매니져는 온도·습도로도 경보를 올리는데 이 둘은 판독 채널이 아니다.
 * 도면 패널(SitePlanPanel `levelOf`)과 같은 규칙이다.
 */
export function nodeAlertLevel(nodeId: number, alerts: readonly Alert[]): Level {
  let worst: Level = 0;
  for (const a of alerts) {
    if (a.node !== nodeId || !countsForGas(a)) continue;
    const lv = clampLevel(a.level);
    if (lv > worst) worst = lv;
  }
  return worst;
}

/**
 * 표시 등급 = max(경보 등급, RI 등급). `ri` 가 멈춘 값이면 호출 쪽이 `null` 을 넘긴다 —
 * 그러면 경보 등급만 남는다(멈춘 RI 가 색을 정하지 않게).
 */
export function nodeLevel(alertLv: Level, ri: number | null | undefined): Level {
  const r = riToLevel(ri);
  return (r > alertLv ? r : alertLv) as Level;
}

/**
 * 층별 위험도 — 그 층 가스들의 `contributors`(가스별 BetP, RI 와 같은 0~1 척도) 중 최고값.
 * 엔진에는 층 축이 없다(가스가 층에 1:1 로 배정돼 있어 표시 계층에서만 접는다).
 * 채점되지 않은 층(값이 하나도 없음)은 null — 0 으로 두면 「정상」으로 읽힌다.
 */
export function tierRisk(
  contributors: Readonly<Record<string, number>> | null | undefined,
): Record<Tier, number | null> {
  const out: Record<Tier, number | null> = { top: null, mid: null, bot: null, etc: null };
  if (!contributors) return out;
  for (const [gas, v] of Object.entries(contributors)) {
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    const tier = tierOf(gas);
    const cur = out[tier];
    if (cur == null || v > cur) out[tier] = v;
  }
  return out;
}

/** 가스 하나의 채점 — `contributors` 의 한 칸. */
export interface GasContribution {
  /** 정규화한 코드(대문자 ASCII). */
  gas: string;
  /** 이 가스의 BetP(0~1). */
  betp: number;
}

/**
 * 위험 기여 상위 n 가스 — BetP 큰 순(같으면 층 표시 순). 노드별 위험도 패널의 「주요 가스」와
 * 현장 헤드라인의 「최고 가스」가 같은 순서를 쓰게 여기 둔다.
 */
export function topContributors(
  contributors: Readonly<Record<string, number>> | null | undefined,
  n = 3,
): GasContribution[] {
  if (!contributors) return [];
  const rows: GasContribution[] = [];
  for (const [gas, v] of Object.entries(contributors)) {
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    rows.push({ gas: normGas(gas), betp: v });
  }
  rows.sort((a, b) => b.betp - a.betp || gasOrderIndex(a.gas) - gasOrderIndex(b.gas));
  return rows.slice(0, Math.max(0, n));
}
